import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";
import { fieldMaxLength, requiredField } from "../../../infrastructure/helpers/yupHelper";
import { IGoal, IGoalsDataSource } from "../models/goalModels";
import { GoalDialogMode } from "./useGoalsOverview";

/** Mirrors UserGoal.TextMaxLength on the API. */
export const GOAL_TEXT_MAX_LENGTH = 500;

const schema = yup.object({
  text: yup
    .string()
    .trim()
    .required(requiredField("Текст"))
    .max(GOAL_TEXT_MAX_LENGTH, fieldMaxLength("Текст", GOAL_TEXT_MAX_LENGTH)),
});

type GoalTextFormModel = yup.InferType<typeof schema>;

interface IUseGoalTextDialog {
  mode: GoalDialogMode | null;
  dataSource: IGoalsDataSource;
  onHide: () => void;
}

/** The add / edit form for one goal or sub-goal. Staff only: it needs `dataSource.editing`. */
export const useGoalTextDialog = ({ mode, dataSource, onHide }: IUseGoalTextDialog) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useApiMutation<IGoal[]>();

  const form = useForm<GoalTextFormModel>({
    defaultValues: { text: "" },
    resolver: yupResolver(schema),
  });

  // Each opening starts from the goal's text (edit) or empty (add).
  useEffect(() => {
    if (mode) {
      form.reset({ text: mode.kind === "edit" ? mode.goal.text : "" });
    }
  }, [mode, form]);

  const header =
    mode?.kind === "edit" ? "Редакция на цел" : mode?.parent ? "Нова подцел" : "Нова цел";

  const handleSubmit = form.handleSubmit(({ text }) => {
    const editing = dataSource.editing;
    if (!editing || !mode) return;

    mutate(
      mode.kind === "edit"
        ? {
            endpoint: editing.editEndpoint,
            method: "put",
            payload: { id: mode.goal.id, text },
            successMessage: "Целта е запазена.",
          }
        : {
            endpoint: editing.addEndpoint,
            method: "post",
            payload: { userId: editing.userId, parentGoalId: mode.parent?.id ?? null, text },
            successMessage: "Целта е добавена.",
          },
      {
        onSuccess: (tree) => {
          queryClient.setQueryData(dataSource.listQueryKey, tree);
          onHide();
        },
      },
    );
  });

  return {
    form,
    header,
    parentText: mode?.kind === "add" ? mode.parent?.text : undefined,
    errorMessage: form.formState.errors.text?.message,
    isLoading,
    handleSubmit,
  };
};
